import React, { useEffect, useState, useRef } from 'react';
import { Label } from '../input-text/input.styled';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import draftToHtml from 'draftjs-to-html';
import { convertToRaw, EditorState, ContentState, Modifier, genKey } from 'draft-js';
import { stateFromHTML } from 'draft-js-import-html';
import _, { isEmpty } from 'lodash';

interface RichTextType {
  name: string;
  setFieldValue: (name: string, value: string) => void;
  value: string;
  error?: any;
  touched?: any;
  initialContentState?: any;
  label?: string;
  id?: string;
}

const RichTextEditor = ({
  name,
  setFieldValue,
  value,
  error,
  touched,
  label,
  initialContentState,
  id,
}: RichTextType): JSX.Element => {
  const isFirstTime = useRef(true);
  const counter = useRef(0);

  const [editorState, setEditorState] = useState<EditorState>();

  const getCurrentHtml = () => {
    if (!editorState) return '';

    const contentState = editorState.getCurrentContent();
    if (contentState.hasText() || contentState.getBlockMap().size > 1) {
      const rawContentState = convertToRaw(contentState);
      const htmlContent = draftToHtml(rawContentState);
      const content = htmlContent
        .replace(/<p><\/p>/g, '<div style="margin: 1em 0px; display: flex;"></div>')
        .replace(/<p>/g, '<p style="margin: 1em 0px;">')
        .replace(/<li>/g, '<li style="margin: 1em 0px;">');
      return content;
    }
    return '';
  };

  const onEditorStateChange = (newEditorState: EditorState): void => {
    setEditorState(newEditorState);
    const html = getCurrentHtml();
    setFieldValue(name, html);
  };

  // Process HTML to preserve empty paragraphs with spacing
  const preprocessHtml = (html: string) => {
    // Mark empty divs and paragraphs with special content that won't be stripped
    return html
      .replace(
        /<div style="margin: 1em 0px; display: flex;"><\/div>/g,
        '<div style="margin: 1em 0px; display: flex;">&#8203;</div>'
      )
      .replace(/<p style="margin: 1em 0px;"><\/p>/g, '<p style="margin: 1em 0px;">&#8203;</p>');
  };

  useEffect(() => {
    counter.current++;
    if (!_.isEmpty(value) && counter.current <= 2) {
      isFirstTime.current = false;
      try {
        // Use stateFromHTML instead of convertFromHTML to better handle empty blocks
        const processedHtml = preprocessHtml(value);
        const contentState = stateFromHTML(processedHtml);
        setEditorState(EditorState.createWithContent(contentState));
      } catch (error) {
        console.error('Error parsing HTML content:', error);
        setEditorState(EditorState.createEmpty());
      }
    } else if (counter.current <= 2 && _.isEmpty(value)) {
      setEditorState(EditorState.createEmpty());
    }
  }, [value]);

  return (
    <div>
      <>
        <Label>{label ? label : 'Description'}</Label>
        <Editor
          id={id}
          key={`editor-${counter.current}`}
          data
          editorState={editorState}
          placeholder='Your text here...'
          toolbarClassName='toolbarClassName'
          wrapperClassName={`wrapperClassName  ${
            Boolean(error) && Boolean(touched) ? 'border border-red-700' : 'border'
          }`}
          editorClassName='editorClassName px-4 min-h-[80px] text-[16px] '
          onEditorStateChange={onEditorStateChange}
          toolbar={{
            options: ['inline', 'list'],
            inline: {
              options: ['bold', 'italic', 'underline'],
              bold: { className: 'customBold' },
            },
            textAlign: { inDropdown: true },
          }}
          stripPastedStyles={false}
          preserveWhitespace={true}
        />
      </>
    </div>
  );
};

export default RichTextEditor;
